export type FlowUxSurface = 'catalog' | 'canvas' | 'inspector' | 'quick-flow' | 'test';

export type FlowUxEvent =
	| {
			name: 'catalog_opened';
			surface: 'catalog';
			entryPoint: 'toolbar' | 'canvas' | 'connection' | 'unknown';
	  }
	| {
			name: 'catalog_view_changed';
			surface: 'catalog';
			view: string;
			browsePath?: 'all' | 'rooms' | 'criteria';
			depth?: number;
	  }
	| {
			name: 'catalog_search_changed';
			surface: 'catalog';
			queryLength: number;
			queryWords: number;
			resultCount: number;
			role?: 'when' | 'and' | 'do';
	  }
	| {
			name: 'catalog_selection';
			surface: 'catalog';
			paletteKey: string;
			view: string;
			browsePath?: 'all' | 'rooms' | 'criteria';
			depth?: number;
			resultCount: number;
			hadSearch: boolean;
			elapsedMs: number;
	  }
	| {
			name: 'catalog_abandoned';
			surface: 'catalog';
			view: string;
			browsePath?: 'all' | 'rooms' | 'criteria';
			depth?: number;
			hadSearch: boolean;
			elapsedMs: number;
	  }
	| {
			name: 'placement_started' | 'placement_completed' | 'placement_cancelled';
			surface: 'canvas';
			paletteKey: string;
			kind: 'node' | 'template';
	  }
	| {
			name: 'save_validation_failed';
			surface: 'canvas';
			reason:
				| 'flow-limit'
				| 'island-limit'
				| 'missing-scene'
				| 'workspace'
				| 'scene-structure'
				| 'uninterpretable';
			issueCount: number;
	  }
	| {
			name: 'configuration_opened' | 'configuration_applied' | 'configuration_abandoned';
			surface: 'inspector';
			paletteKey: string;
			elapsedMs: number;
	  }
	| {
			name: 'run_started' | 'run_completed' | 'run_failed';
			surface: 'test';
			elapsedMs: number;
			nodeCount: number;
	  };

export interface RecordedFlowUxEvent {
	event: FlowUxEvent;
	timestamp: number;
	sessionSequence: number;
}

export type FlowUxTelemetrySink = (record: RecordedFlowUxEvent) => void;

const MAX_SESSION_EVENTS = 250;
let sink: FlowUxTelemetrySink | undefined;
let sequence = 0;
const sessionEvents: RecordedFlowUxEvent[] = [];

/**
 * Installs the product analytics adapter. The Flow editor itself never imports a vendor SDK.
 * Passing no sink restores the privacy-safe, in-memory-only default.
 */
export function configureFlowUxTelemetry(nextSink?: FlowUxTelemetrySink): void {
	sink = nextSink;
}

export function trackFlowUxEvent(event: FlowUxEvent): RecordedFlowUxEvent {
	const record = {
		event,
		timestamp: Date.now(),
		sessionSequence: ++sequence,
	};
	sessionEvents.push(record);
	if (sessionEvents.length > MAX_SESSION_EVENTS) sessionEvents.shift();
	sink?.(record);
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new CustomEvent('shelly:flow-ux', { detail: record }));
	}
	return record;
}

export function getFlowUxSessionEvents(): readonly RecordedFlowUxEvent[] {
	return sessionEvents;
}

export function resetFlowUxTelemetryForTests(): void {
	sink = undefined;
	sequence = 0;
	sessionEvents.splice(0, sessionEvents.length);
}

export function summarizePrivateSearchQuery(query: string): {
	queryLength: number;
	queryWords: number;
} {
	const normalized = query.trim();
	return {
		queryLength: normalized.length,
		queryWords: normalized ? normalized.split(/\s+/).length : 0,
	};
}
